import { createContext, memo, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { QueryParamNames } from '~/constants/search';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type AssetModalVersionsPanelContextValue = {
  isVersionsPanelOpen: boolean;
  openVersionsPanel: () => void;
  closeVersionsPanel: () => void;
};

const DEFAULT_CONTEXT_VALUE: AssetModalVersionsPanelContextValue = {
  isVersionsPanelOpen: false,
  openVersionsPanel: () => {},
  closeVersionsPanel: () => {},
};

export const AssetModalVersionsPanelContext = createContext<AssetModalVersionsPanelContextValue>(DEFAULT_CONTEXT_VALUE);

export const useAssetModalVersionsPanel = () => {
  const ctx = useContext(AssetModalVersionsPanelContext);

  if (ctx === DEFAULT_CONTEXT_VALUE) {
    const error = new Error('useAssetModalVersionsPanel must be used within AssetModalVersionsPanelContextProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Asset Modal versions panel context not found',
    });
  }

  return ctx;
};

export type AssetModalVersionsPanelContextProviderProps = { children: ReactNode };

export const AssetModalVersionsPanelContextProvider = memo(
  ({ children }: AssetModalVersionsPanelContextProviderProps) => {
    const [isVersionsPanelOpen, setIsVersionsPanelOpen] = useState(false);

    // Read from URL on mount to open the panel if the legacy `?versions=` deep link is present
    useEffect(() => {
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.get(QueryParamNames.versions)) {
        setIsVersionsPanelOpen(true);
      }
    }, []);

    const openVersionsPanel = useCallback(() => {
      setIsVersionsPanelOpen(true);
    }, []);

    const closeVersionsPanel = useCallback(() => {
      setIsVersionsPanelOpen(false);
    }, []);

    const contextValue = useMemo(
      () => ({ isVersionsPanelOpen, openVersionsPanel, closeVersionsPanel }),
      [isVersionsPanelOpen, openVersionsPanel, closeVersionsPanel],
    );

    return (
      <AssetModalVersionsPanelContext.Provider value={contextValue}>{children}</AssetModalVersionsPanelContext.Provider>
    );
  },
);

AssetModalVersionsPanelContextProvider.displayName = 'AssetModalVersionsPanelContextProvider';
